<script setup lang="ts">
import type { Locale, TextFieldConfig } from '@/forms/types'

const props = defineProps<{
  field: TextFieldConfig
  locale: Locale
  modelValue: string
  error?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const errorId = `${props.field.id}-error`
const hintId = `${props.field.id}-hint`
</script>

<template>
  <div class="field" :class="{ 'has-error': !!error }">
    <label class="field-label" :for="field.id">
      {{ field.label[locale] }}
      <span v-if="field.required" aria-hidden="true" class="req">*</span>
    </label>
    <p v-if="field.description" :id="hintId" class="field-hint">{{ field.description[locale] }}</p>
    <input
      :id="field.id"
      class="field-input"
      :type="field.type"
      :value="modelValue"
      :placeholder="field.placeholder?.[locale] ?? ''"
      :required="field.required"
      :aria-required="field.required ? 'true' : undefined"
      :aria-invalid="error ? 'true' : undefined"
      :aria-describedby="
        [error ? errorId : '', field.description ? hintId : ''].filter(Boolean).join(' ') ||
        undefined
      "
      :autocomplete="field.type === 'email' ? 'email' : 'off'"
      @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
    />
    <p v-if="error" :id="errorId" class="field-error" role="alert">{{ error }}</p>
  </div>
</template>

<style scoped>
.field {
  margin-bottom: var(--space-5);
}
</style>
